import moment from "moment";
import produce from "immer";
import * as React from "react";
import Tooltip from "@mui/material/Tooltip";
import { push } from "connected-react-router";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import BlockIcon from "@mui/icons-material/Block";
import { connect, useDispatch } from "react-redux";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Box, Button, Menu, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PrintIcon from "@mui/icons-material/Print";
import {
  getMedicalTestById,
  patchMedicalTest,
  updateMedicalTestsRecordSample
} from "../../actions/medicalTest";
import Panel from "../../components/Panel";
import { tl } from "../../components/translate";
import ShowVitals from "./Assessment/ShowVitals";
import { LabStatuses } from "../../interfaces/Lab";
import { getAppBaseUrl } from "../../helpers/urls";
import { rupeeDisplay } from "../../helpers/rupee";
import BillShow from "../Billing/BillShow/BillShow";
import { uploadClientImage } from "../../actions/client";
import ClientInfo from "../Client/ClientInfo/ClientInfo";
import ShowAssessment from "./Assessment/ShowAssessment";
import ShowTestResult from "./Assessment/ShowTestResult";
import ClientCreateEdit from "../Client/ClientCreateEdit";
import OkhatiDialog from "../../components/Dialog/Dialog";
import { BillType } from "../../interfaces/BillInterfaces";
import { notificationAdd } from "../../actions/notification";
import MedicalTestStatusTypes from "../../interfaces/Medical";
import ShowVaccinations from "./Vaccination/ShowVaccinations";
import { clientFullNameSelector } from "../../reducers/client";
import Collapse from "../../components/CollapseBar/CollapseBar";
import { getBillByNumber, getClientBalance } from "../../api/bill";
import MedicalPrintsButtonGroup from "./Print/MedicalDetailsButtonGroup";
import CopyToClipboard from "../../components/ClipboardCopy/ClipboardCopy";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import ForeignEmploymentPrint from "./Print/Templates/ForeignEmploymentPrint";
import CalendarDropdown from "../../components/CalendarDropdown/CalendarDropdown";
import ClientImageUploader from "../../components/ImageUploader/ClientImageUploader";

// css
import styles from "./Lab.module.css";
import Can from "../Policy/Can";
import MedicalSampleStickerPrint from "./MedicalSampleStickerPrint";

interface Props {
  handleClose: () => void;
  id: number;
  medicalTest: any;
  navigateTo: (v) => void;
  uploadPhoto: (id: number, image: string) => void;
  updateSampleCollected: (
    medicalTestId: number,
    status: string,
    stateRemark?: string,
    id?: number,
    name?: string
  ) => void;
}

function MedicalDetails({
  handleClose,
  id,
  medicalTest,
  navigateTo,
  uploadPhoto,
  updateSampleCollected
}: Props) {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();

  // local states
  const [saving, setSaving] = React.useState(false);
  const [clientBalance, setClientBalance] = React.useState(0);
  const [bill, setBill] = React.useState<BillType | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [clientEditMode, setClientEditMode] = React.useState(false);
  const [billNumber, setBillNumber] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [collapseAssessment, setCollapseAssessment] = React.useState(true);
  const [showClientInfoPanel, setShowClientInfoPanel] = React.useState(false);
  const [collapseVaccinations, setCollapseVaccinations] = React.useState(true);
  const [collapseTests, setCollapseTests] = React.useState(true);
  const [collapseVitals, setCollapseVitals] = React.useState(true);

  const examDate = React.useRef(null);
  const authorizedSP = rc?.settings?.medicalSettings;
  const clientInfoViewHandle = () => {
    setShowClientInfoPanel(false);
  };
  const [printMenuAnchorEl, setPrintMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleCloseMenu = () => {
    setPrintMenuAnchorEl(null);
  };

  // effects
  React.useEffect(() => {
    const fetchClientBalance = async () => {
      try {
        const { balance } = await getClientBalance(Number(medicalTest?.client?.id));
        setClientBalance(Number(balance));
      } catch (err) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err?.message || err?.data?.message || "Unable to fetch client balance",
            autoTimeout: true
          })
        );
      }
    };

    if (medicalTest?.client?.id) {
      fetchClientBalance();
    }
  }, [dispatch, medicalTest?.client?.id]);

  React.useEffect(() => {
    const fetchBill = async () => {
      try {
        const billData = await getBillByNumber(billNumber as string);
        setBill(billData);
      } catch (err) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            message: err.data?.message || "Something went wrong when getting bill details.",
            autoTimeout: true,
            variant: "error"
          })
        );
      }
    };

    if (billNumber) {
      fetchBill();
    }
  }, [billNumber, dispatch]);

  React.useEffect(() => {
    if (id) {
      dispatch(getMedicalTestById(id));
    }
  }, [id, dispatch]);

  if (!medicalTest) {
    return null;
  }

  return (
    <>
      <Panel
        titleJsx={
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography>{tl("medical.head.medicaltest")}</Typography>
          </Box>
        }
        genericButton={
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Tooltip title="Medical Sample Sticker" aria-label="medical-sample-sticker">
                <IconButton
                  color="inherit"
                  onClick={(event) => setPrintMenuAnchorEl(event.currentTarget)}
                  data-testomation="printButton"
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              {medicalTest.results.assessment && (
                <CopyToClipboard
                  TooltipProps={{
                    title: "Medical report link is copied to clipboard!"
                  }}
                >
                  {({ copy }) => (
                    <Tooltip title="Copy link" aria-label="copylink">
                      <IconButton
                        color="inherit"
                        onClick={() => {
                          copy(`${getAppBaseUrl()}/my-medical/${medicalTest.uuid}`);
                        }}
                      >
                        <FileCopyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </CopyToClipboard>
              )}
              {medicalTest.status !== MedicalTestStatusTypes.CANCELED && (
                <Tooltip
                  slotProps={{ popper: { disablePortal: true } }}
                  style={{ zIndex: 1, padding: 0 }}
                  title={tl("medical.cancelMedicalTest")}
                >
                  <IconButton
                    color="error"
                    onClick={() => setIsDialogOpen(true)}
                    data-testmation="cancelmedicaltest"
                    size="small"
                  >
                    <BlockIcon className={styles.icon} />
                  </IconButton>
                </Tooltip>
              )}
              {medicalTest.status === MedicalTestStatusTypes.RESULT_READY && (
                <>
                  <Tooltip
                    slotProps={{ popper: { disablePortal: true } }}
                    style={{ zIndex: 1, padding: 0 }}
                    title={tl("medical.editExamDate")}
                    data-testmation="editExamDate"
                  >
                    <IconButton
                      color="inherit"
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      size="large"
                    >
                      <EditIcon className={styles.icon} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                  >
                    <Box alignItems="center" gap={1} p={1} width="210px" display="flex">
                      <CalendarDropdown
                        disable={false}
                        TextFieldProps={{ fullWidth: true, variant: "outlined", margin: "dense" }}
                        date={
                          medicalTest.results.examDate
                            ? new Date(medicalTest.results.examDate)
                            : new Date()
                        }
                        onChange={(newDate) => {
                          examDate.current = moment(newDate).toISOString();
                        }}
                      />
                      <Button
                        disabled={saving}
                        onClick={async () => {
                          setSaving(true);
                          const updatedResult = produce(medicalTest.results, (draft) => {
                            draft.examDate = examDate.current;
                          });
                          const toPost = {
                            attachments: [],
                            authorizedSP1Id: authorizedSP.authorizedSP1,
                            authorizedSP2Id: authorizedSP.authorizedSP2,
                            authorizedSP3Id: authorizedSP.authorizedSP3,
                            authorizedSP4Id: authorizedSP.authorizedSP4,
                            final: medicalTest.status !== LabStatuses.INCOMPLETE_RESULT,
                            updatedTestResults: updatedResult
                          };
                          await dispatch(patchMedicalTest(toPost, id));
                          setAnchorEl(null);
                          setSaving(false);
                        }}
                        size="small"
                        variant="contained"
                      >
                        Save
                      </Button>
                    </Box>
                  </Menu>
                </>
              )}
            </Box>
          </>
        }
        onClose={handleClose}
        wrapperStyle={{ minWidth: "300px", width: "100%" }}
        contentStyle={{ hight: "calc(100vh - 50px)", pt: 0, px: 0 }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              px: 4,
              py: 1.25,
              borderBottom: 1,
              borderColor: "divider"
            }}
          >
            <MedicalPrintsButtonGroup
              medicalTestStatus={medicalTest.status?.toLowerCase()}
              medicalTestId={medicalTest?.id}
            />
          </Box>

          <Box sx={{ px: 4 }}>
            <Can
              partialCheck
              policyAccessKey={[
                "medical:manageLabResult",
                "medical:manageAssessment",
                "medical:manageXrayResult"
              ]}
            >
              <Box display="flex" justifyContent="flex-end">
                {medicalTest.status === MedicalTestStatusTypes.ORDERED && (
                  <Button
                    data-testmation="addTestResult"
                    variant="contained"
                    justify-content="flex-end"
                    color="primary"
                    onClick={() => navigateTo(`/medical/entry/${id}`)}
                  >
                    {tl("lab.enterTestResults")}
                  </Button>
                )}
              </Box>
            </Can>
            <Typography>
              <Box display="flex">
                <Typography style={{ width: "120px" }}>{tl("lab.testId")}</Typography>
                <Typography>
                  <Box component="span" fontWeight="600">
                    {id}
                  </Box>
                </Typography>
              </Box>

              {medicalTest?.results?.billNumber && (
                <>
                  <Box display="flex" mt={2}>
                    <Typography style={{ width: "120px" }}>{tl("billnumber")}</Typography>
                    <Typography
                      style={{
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: "pointer"
                      }}
                      onClick={() => {
                        setBillNumber(medicalTest?.results?.billNumber || null);
                      }}
                    >
                      {medicalTest.results.billNumber}
                    </Typography>
                    <Tooltip
                      title={
                        <Typography style={{ fontSize: "0.675rem" }}>
                          Note: This lab test cannot be edited because bill has already been
                          created.
                        </Typography>
                      }
                      sx={{
                        marginLeft: "10px",
                        color: "#676766"
                      }}
                    >
                      <InfoIcon />
                    </Tooltip>
                  </Box>
                </>
              )}

              {clientBalance < 0 && (
                <Box sx={{ display: "flex" }}>
                  <Typography style={{ width: "120px" }}>{tl("billing.previousDueAmt")}</Typography>
                  <Typography
                    style={{
                      fontWeight: 600
                    }}
                  >
                    {rupeeDisplay(clientBalance)}
                  </Typography>
                </Box>
              )}

              <Box display="flex" mt="16px">
                <Box flexDirection="column" justifyContent="space-between" mr="20px">
                  <Box display="flex">
                    <Typography style={{ width: "120px" }}>{tl("lab.client")}</Typography>
                    <Typography
                      style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => {
                        setShowClientInfoPanel(true);
                      }}
                    >
                      {clientFullNameSelector(medicalTest?.client).toUpperCase()}
                    </Typography>
                  </Box>

                  <Box display="flex">
                    <Typography style={{ width: "120px" }}>{tl("lab.phoneNo")}</Typography>
                    <Typography>
                      <Box component="span" fontWeight="600">
                        {medicalTest.client?.phone}
                      </Box>
                    </Typography>
                  </Box>
                  <Box display="flex">
                    <Typography style={{ width: "120px" }}>{tl("lab.address")}</Typography>
                    <Typography>
                      <Box component="span" fontWeight="600">
                        {medicalTest.client?.address}
                      </Box>
                    </Typography>
                  </Box>
                  {medicalTest.client.email && (
                    <>
                      <Box display="flex">
                        <Typography style={{ width: "120px" }}>{tl("medical.email")}</Typography>
                        <Typography>
                          <Box component="span" fontWeight="600">
                            {medicalTest.client.email}
                          </Box>
                        </Typography>
                      </Box>
                    </>
                  )}
                  <Box display="flex">
                    <Typography style={{ width: "120px" }}>{tl("medical.clientId")}</Typography>
                    <Typography>
                      <Box component="span" fontWeight="600">
                        {medicalTest.client.id}
                      </Box>
                    </Typography>
                  </Box>
                </Box>

                <Box justifyContent="flex-end">
                  <ClientImageUploader
                    client={medicalTest.client}
                    action={(image) => {
                      uploadPhoto(medicalTest.clientId, image);
                    }}
                    initialImage={medicalTest.customer?.customerDetails?.profileImageS3Url || ""}
                  />
                </Box>
              </Box>

              {medicalTest.referrers && (
                <>
                  <Box display="flex">
                    <Typography style={{ width: "120px" }}>{tl("medical.referredBy")}</Typography>
                    <Typography>
                      <Box component="span" fontWeight="600">
                        {medicalTest.referrers}
                      </Box>
                    </Typography>
                  </Box>
                </>
              )}
            </Typography>

            {medicalTest.status === MedicalTestStatusTypes.ORDERED ||
            medicalTest.status === MedicalTestStatusTypes.CANCELED ? (
              <>
                <Typography style={{ paddingTop: "32px" }}>
                  <Box component="span" fontWeight="600">
                    {tl("lab.testParticulars")}
                  </Box>
                </Typography>
                <Typography>
                  <Box>Medical Test</Box>
                  {medicalTest.results.data[0].labTests.map((item) => (
                    <Box component="span" key={item.name}>
                      {item.name},
                    </Box>
                  ))}
                </Typography>
              </>
            ) : (
              <Box marginTop="32px">
                <Collapse
                  label={
                    <Box display="flex">
                      <Typography>{tl("medical.testResult")}</Typography>
                      <CheckCircleIcon
                        htmlColor={
                          medicalTest.status !== MedicalTestStatusTypes.INCOMPLETE_RESULT
                            ? "#009654"
                            : "#808080"
                        }
                        style={{ marginLeft: "16px" }}
                      />
                    </Box>
                  }
                  collapsed={collapseTests}
                >
                  <ShowTestResult medicalTest={medicalTest} navigateTo={navigateTo} id={id} />
                </Collapse>
                <Collapse
                  label={
                    <Box display="flex">
                      <Typography>{tl("medical.vitals")}</Typography>
                      {medicalTest.results.vitals && (
                        <CheckCircleIcon htmlColor="#009654" style={{ marginLeft: "16px" }} />
                      )}
                    </Box>
                  }
                  collapsed={collapseVitals}
                  onCollapse={() => {
                    navigateTo({ pathname: `/medical/details/${id}`, search: "" });
                  }}
                >
                  <ShowVitals
                    vitals={medicalTest?.results?.vitals ? medicalTest?.results?.vitals : ""}
                    nextAction={() => {
                      setCollapseTests(true);
                      setCollapseVitals(true);
                      setCollapseVaccinations(false);
                    }}
                    id={id}
                  />
                </Collapse>
                <Collapse
                  label={
                    <Box display="flex">
                      <Typography>{tl("medical.updateVaccinations")}</Typography>
                      {medicalTest?.results?.vaccinations && (
                        <CheckCircleIcon htmlColor="#009654" style={{ marginLeft: "16px" }} />
                      )}
                    </Box>
                  }
                  collapsed={collapseVaccinations}
                >
                  <ShowVaccinations
                    medicalTestId={id}
                    vaccinations={medicalTest?.results?.vaccinations || []}
                    nextAction={() => {
                      setCollapseVitals(true);
                      setCollapseVaccinations(true);
                      setCollapseAssessment(false);
                    }}
                  />
                </Collapse>
                <Collapse
                  label={
                    <Box display="flex">
                      <Typography>{tl("medical.assessment")}</Typography>
                      {medicalTest.results.assessment && (
                        <CheckCircleIcon htmlColor="#009654" style={{ marginLeft: "16px" }} />
                      )}
                    </Box>
                  }
                  collapsed={collapseAssessment}
                >
                  <ShowAssessment
                    assessment={
                      medicalTest?.results?.assessment ? medicalTest?.results?.assessment : ""
                    }
                    id={id}
                  />
                </Collapse>
                <Box display="flex" justifyContent="flex-end">
                  {medicalTest.results.assessment &&
                    medicalTest.status === MedicalTestStatusTypes.RESULT_READY && (
                      <Can policyAccessKey="medical:printMedicalReport">
                        <Box>
                          <ForeignEmploymentPrint
                            buttonText={
                              <Button variant="contained" color="primary">
                                {tl("lab.print")}
                              </Button>
                            }
                            labRecord={medicalTest}
                            assessment={[medicalTest.results.assessment]}
                          />
                        </Box>
                      </Can>
                    )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Menu
          anchorEl={printMenuAnchorEl}
          open={Boolean(printMenuAnchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          <MedicalSampleStickerPrint
            labTestRecordId={medicalTest.id}
            handleCloseMenu={handleCloseMenu}
            client={medicalTest.client}
          />
          <MedicalSampleStickerPrint
            labTestRecordId={medicalTest.id}
            isMultiplePrint
            handleCloseMenu={handleCloseMenu}
            client={medicalTest.client}
          />
        </Menu>
      </Panel>

      {showClientInfoPanel &&
        medicalTest?.client &&
        (clientEditMode ? (
          <ClientCreateEdit
            setEditMode={setClientEditMode}
            clientId={medicalTest?.client?.id}
            mode="edit"
            onCancel={() => {
              setClientEditMode(false);
            }}
            stayOnCurrentPage
          />
        ) : (
          <ClientInfo
            setEditMode={setClientEditMode}
            id={medicalTest?.client.id}
            handleViewClose={clientInfoViewHandle}
            wrapperStyle={{ minWidth: "645px", maxWidth: "645px" }}
            stayOnCurrentPage
          />
        ))}

      {billNumber && bill && (
        <BillShow
          billId={bill.id}
          handleViewClose={() => setBillNumber(null)}
          hideActions
          wrapperStyle={{ minWidth: "660px", maxWidth: "660px" }}
          billData={bill}
        />
      )}

      {isDialogOpen && (
        <OkhatiDialog
          title={tl("medical.cancelMedicalTest")}
          description={tl("medical.areYouSureCancel")}
          next={async () => {
            await updateSampleCollected(id, MedicalTestStatusTypes.CANCELED);
            setIsDialogOpen(false);
          }}
          cancel={() => {
            setIsDialogOpen(false);
          }}
          readMode={false}
        />
      )}
    </>
  );
}

function mapStateToProps(state, ownProps) {
  const medicalTest = state.medicalTest.collection.find((item) => item.id === Number(ownProps.id));

  return {
    medicalTest
  };
}

const mapDispatchToProps = (dispatch) => ({
  navigateTo: (url) => {
    dispatch(push(url));
  },
  uploadPhoto: (id, image) => dispatch(uploadClientImage(id, image)),
  updateTest: (data, id) => dispatch(patchMedicalTest(data, id)),
  updateSampleCollected: (medicalTestId, status, remark, id, name) => {
    dispatch(updateMedicalTestsRecordSample({ medicalTestId, status, remark, id, name }));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(MedicalDetails);
